import React, { useState } from 'react';
import { AlertTriangle, X, Scan, Gift, CheckCircle2, RotateCcw, Download } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { BACKEND_URL } from '../../config';
import { getAdminAuthConfig } from '../../utils/adminAuth';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const ResetMetricsModal = ({ isOpen, onClose, onSuccess, stats }) => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen) return null;

  const isConfirmed = confirmText === 'RESET-ALL-METRICS';

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const config = getAdminAuthConfig();
      config.responseType = 'blob';
      const response = await axios.get(`${API}/admin/export-metrics`, config);

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `tappd-metrics-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('CSV downloaded successfully');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = async () => {
    if (!isConfirmed) return;
    setLoading(true);
    try {
      const config = getAdminAuthConfig();
      const response = await axios.post(`${API}/admin/reset-metrics`, {
        confirm: 'RESET-ALL-METRICS'
      }, config);

      const { deleted } = response.data;
      toast.success(
        `Metrics reset complete: ${deleted.scans} scans, ${deleted.deal_claims} claims deleted`
      );
      setConfirmText('');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Reset failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to reset metrics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-display text-xl text-navy font-bold">Reset All Metrics</h3>
              <p className="text-xs text-navy/50">This action cannot be undone</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Stats Summary */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
          <p className="text-sm font-semibold text-red-800 mb-3">The following data will be permanently deleted:</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Scan className="w-4 h-4 text-red-400" />
              </div>
              <p className="text-lg font-bold text-red-700">{stats?.total_scans || 0}</p>
              <p className="text-xs text-red-500">Scans</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Gift className="w-4 h-4 text-red-400" />
              </div>
              <p className="text-lg font-bold text-red-700">{stats?.total_claims || 0}</p>
              <p className="text-xs text-red-500">Claims</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <CheckCircle2 className="w-4 h-4 text-red-400" />
              </div>
              <p className="text-lg font-bold text-red-700">{stats?.total_redemptions || 0}</p>
              <p className="text-xs text-red-500">Redemptions</p>
            </div>
          </div>
        </div>

        {/* Download CSV before resetting */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-800">Download records first</p>
              <p className="text-xs text-blue-600/70 mt-0.5">Export all scans, claims, and leads as CSV before wiping.</p>
            </div>
            <button
              onClick={handleDownloadCSV}
              disabled={downloading}
              className="flex-shrink-0 ml-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {downloading ? (
                <>
                  <RotateCcw className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download CSV
                </>
              )}
            </button>
          </div>
        </div>

        {/* What else gets wiped */}
        <div className="text-sm text-navy/60 mb-4 space-y-1">
          <p>Also resets: Taps balances, leaderboards, quest progress, business leads, and all denormalized counters across businesses, deals, locations, and users.</p>
          <p className="font-medium text-navy/80">User accounts, business profiles, deals, and hub configurations are preserved.</p>
        </div>

        {/* Confirmation Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-navy mb-2">
            Type <span className="font-mono bg-navy/5 px-1.5 py-0.5 rounded text-red-600">RESET-ALL-METRICS</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESET-ALL-METRICS"
            className="w-full px-4 py-2.5 border border-navy/20 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            disabled={loading}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            className="px-6 py-2"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReset}
            disabled={!isConfirmed || loading}
            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 animate-spin" />
                Resetting...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Reset All Metrics
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResetMetricsModal;
